import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  EventScheduleException,
  EventType,
  ScheduleDeletionPreviewEvent,
  getScheduleDeletionBookingWarning,
  ROLLING_ORPHAN_REASON_SINGULAR,
  ROLLING_ORPHAN_REASON_PLURAL
} from '../../../../../../models/event-type';
import { dateStringFromISO8601 } from '../../../../../../utils';
import { AccountService } from '../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { EventTypeStatus, ScheduleType } from '../../../../../../models/event-type-enums';
import { SimpleMessageModalComponent } from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
// tslint:disable-next-line:max-line-length
import { ScheduleOrphanedEventsModalComponent } from '../../modals/schedule-orphaned-events-modal/schedule-orphaned-events-modal.component';

@Component({
  selector: 'app-event-type-schedule-exceptions',
  templateUrl: './event-type-schedule-exceptions.component.html',
  styleUrls: ['./event-type-schedule-exceptions.component.css']
})
export class EventTypeScheduleExceptionsComponent implements OnInit {
  public eventType: EventType = null;
  public scheduleExceptions: EventScheduleException[];

  public loadingEventType = false;
  public loadingScheduleExceptions = false;

  public notFound = false;
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly ScheduleType = ScheduleType;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    const eventTypeIdInt = parseInt(routeParams.id, 10);
    if (isNaN(eventTypeIdInt)) {
      this.notFound = true;
      return;
    }

    this.loadEventType(eventTypeIdInt);
    this.loadScheduleExceptions(eventTypeIdInt);
  }

  public loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  public loadScheduleExceptions(eventTypeId: number) {
    this.loadingScheduleExceptions = true;

    this.eventTypeService
      .getEventScheduleExceptions(eventTypeId, 1, 100)
      .then(response => {
        this.scheduleExceptions = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingScheduleExceptions = false; });
  }

  /**
   * Remove the given schedule exception.
   *
   * Deleting an exception removes its future events unless they have active
   * reservations. Any events which would be left orphaned (because they have
   * active reservations) are shown to the user in a warning modal first, so
   * they can decide whether to proceed.
   *
   * On a rolling event type the future events are deleted along with their
   * bookings, so there is usually nothing to list; the user is told how many
   * events and bookings the deletion will remove instead.
   */
  public removeScheduleException(scheduleException: EventScheduleException) {
    this.eventTypeService
      .getEventScheduleExceptionDeletionPreview(
        this.eventType.id,
        scheduleException.id
      )
      .then(preview => {
        const bookingWarning = getScheduleDeletionBookingWarning(
          'schedule exception',
          preview
        );

        if (preview.remaining_events.length > 0) {
          this.openOrphanedEventsWarning(
            scheduleException,
            preview.remaining_events,
            bookingWarning
          );
        } else if (confirm(
          bookingWarning
            ? `${bookingWarning} Are you sure you want to continue?`
            : 'Are you sure you want to remove this schedule exception? This action cannot be undone.'
        )) {
          this.deleteScheduleException(scheduleException);
        }
      })
      .catch(() => {
        // The preview is advisory — if it cannot be loaded, fall back to the
        // plain confirmation rather than blocking deletion entirely.
        if (confirm(
          'Are you sure you want to remove this schedule exception? This action cannot be undone.'
        )) {
          this.deleteScheduleException(scheduleException);
        }
      });
  }

  /**
   * Warn the user that deleting the exception will leave the given events
   * orphaned, and delete the exception if they choose to proceed.
   *
   * The booking warning is carried into the modal rather than dropped: on a
   * rolling event type the deletion both destroys booked events and leaves a
   * benefit-granting one behind, and staff need to be told about both.
   */
  private openOrphanedEventsWarning(
    scheduleException: EventScheduleException,
    remainingEvents: ScheduleDeletionPreviewEvent[],
    bookingWarning: string | null
  ) {
    const modalRef = this.modalService.open(
      ScheduleOrphanedEventsModalComponent,
      { size: 'lg' }
    );
    modalRef.componentInstance.scheduleName = scheduleException.name;
    modalRef.componentInstance.subjectNoun = 'schedule exception';
    modalRef.componentInstance.remainingEvents = remainingEvents;
    modalRef.componentInstance.bookingWarning = bookingWarning || '';

    if (this.eventType.schedule_type === ScheduleType.rolling) {
      modalRef.componentInstance.remainingReasonSingular =
        ROLLING_ORPHAN_REASON_SINGULAR;
      modalRef.componentInstance.remainingReasonPlural =
        ROLLING_ORPHAN_REASON_PLURAL;
    }

    modalRef.componentInstance.confirmedCallback = () => {
      this.deleteScheduleException(scheduleException);
    };
  }

  /**
   * Delete the given schedule exception via the API, removing it from the list
   * on success.
   */
  private deleteScheduleException(scheduleException: EventScheduleException) {
    this.eventTypeService
      .deleteEventScheduleException(scheduleException)
      .then(() => {
        const index = this.scheduleExceptions.indexOf(scheduleException);
        if (index !== -1) {
          this.scheduleExceptions.splice(index, 1);
        }
      })
      .catch(() => {
        this.openErrorModal(
          'Something went wrong while deleting this schedule exception. ' +
          'Please try again.'
        );
      });
  }

  private openErrorModal(message: string) {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Error';
    modalRef.componentInstance.messageText = message;
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
  }

  /**
   * Get the given duration value in human-readable text form.
   */
  public getDurationText(durationMinutes: number): string {
    if ((durationMinutes % 60) === 0) {
      const numHours = durationMinutes / 60;
      if (numHours === 1) {
        return '1 hour';
      } else {
        return numHours.toString() + ' hours';
      }
    } else {
      return durationMinutes.toString() + ' min';
    }
  }
}
